import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import { useMaterialUIController } from "context";
import axios from "axios";
import API_ENDPOINTS from "config/api";

export default function ListaVentas() {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [ventas, setVentas] = useState({ columns: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [ventaSeleccionada, setVentaSeleccionada] = useState(null);
  const [filtroFechaInicio, setFiltroFechaInicio] = useState("");
  const [filtroFechaFin, setFiltroFechaFin] = useState("");
  const [filtroEstatus, setFiltroEstatus] = useState("");

  // Modales
  const [modalDetalle, setModalDetalle] = useState(false);
  const [modalAnular, setModalAnular] = useState(false);
  const [detalleVenta, setDetalleVenta] = useState(null);

  // Función para formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  useEffect(() => {
    fetchVentas();
  }, []);

  const fetchVentas = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtroFechaInicio) params.fecha_inicio = filtroFechaInicio;
      if (filtroFechaFin) params.fecha_fin = filtroFechaFin;
      if (filtroEstatus) params.estatus = filtroEstatus;

      const response = await axios.get(API_ENDPOINTS.VENTAS, { params });
      const ventasData = response.data;

      const rows = ventasData.map((venta) => ({
        id: venta.id,
        factura: (
          <MDTypography variant="button" fontWeight="medium" fontSize="0.875rem">
            {venta.numero_factura || `VENTA-${venta.id}`}
          </MDTypography>
        ),
        fecha: (
          <MDTypography variant="caption" color="text">
            {new Date(venta.fecha_venta || venta.created_at).toLocaleDateString("es-ES", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </MDTypography>
        ),
        cliente: (
          <MDTypography variant="caption" color="text">
            {venta.cliente
              ? `${venta.cliente.nombre} ${venta.cliente.apellido || ""}`
              : "Cliente General"}
          </MDTypography>
        ),
        total: (
          <MDTypography variant="button" fontWeight="bold" color="success" fontSize="0.875rem">
            {formatCurrency(parseFloat(venta.total))}
          </MDTypography>
        ),
        estatus: (
          <Chip
            label={venta.estatus.toUpperCase()}
            color={
              venta.estatus === "pagada"
                ? "success"
                : venta.estatus === "pendiente"
                ? "warning"
                : "error"
            }
            size="small"
          />
        ),
        accion: (
          <IconButton onClick={(e) => handleOpenMenu(e, venta)} size="small">
            <Icon fontSize="small">more_vert</Icon>
          </IconButton>
        ),
        _rawData: venta,
      }));

      const columns = [
        { Header: "Factura", accessor: "factura", width: "15%" },
        { Header: "Fecha", accessor: "fecha", width: "20%" },
        { Header: "Cliente", accessor: "cliente", width: "25%" },
        { Header: "Total", accessor: "total", width: "15%" },
        { Header: "Estatus", accessor: "estatus", width: "15%" },
        { Header: "Acción", accessor: "accion", width: "10%" },
      ];

      setVentas({ columns, rows });
      setLoading(false);
    } catch (error) {
      console.error("Error al cargar ventas:", error);
      setLoading(false);
    }
  };

  const handleOpenMenu = (event, venta) => {
    setMenuAnchor(event.currentTarget);
    setVentaSeleccionada(venta);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setVentaSeleccionada(null);
  };

  const verDetalle = async () => {
    if (ventaSeleccionada) {
      try {
        const response = await axios.get(API_ENDPOINTS.VENTA(ventaSeleccionada.id));
        setDetalleVenta(response.data);
        setModalDetalle(true);
      } catch (error) {
        console.error("Error al cargar detalle:", error);
      }
    }
    handleCloseMenu();
  };

  const imprimirFactura = () => {
    if (ventaSeleccionada) {
      console.log("Imprimir factura:", ventaSeleccionada);
      window.print();
    }
    handleCloseMenu();
  };

  const abrirModalAnular = () => {
    setModalAnular(true);
    handleCloseMenu();
  };

  const anularVenta = async () => {
    if (ventaSeleccionada) {
      try {
        await axios.delete(API_ENDPOINTS.VENTA(ventaSeleccionada.id));
        setModalAnular(false);
        fetchVentas();
      } catch (error) {
        console.error("Error al anular venta:", error);
      }
    }
  };

  const aplicarFiltros = () => {
    fetchVentas();
  };

  const limpiarFiltros = () => {
    setFiltroFechaInicio("");
    setFiltroFechaFin("");
    setFiltroEstatus("");
    setTimeout(() => fetchVentas(), 100);
  };

  return (
    <MDBox>
      <Card sx={{ mb: 2 }}>
        <MDBox p={2}>
          <MDTypography variant="h6" fontWeight="bold" mb={2}>
            Filtros de Búsqueda
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <MDTypography variant="caption" fontWeight="medium" color="text" mb={0.5}>
                Fecha Inicio
              </MDTypography>
              <TextField
                type="date"
                value={filtroFechaInicio}
                onChange={(e) => setFiltroFechaInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MDTypography variant="caption" fontWeight="medium" color="text" mb={0.5}>
                Fecha Fin
              </MDTypography>
              <TextField
                type="date"
                value={filtroFechaFin}
                onChange={(e) => setFiltroFechaFin(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MDTypography variant="caption" fontWeight="medium" color="text" mb={0.5}>
                Estatus
              </MDTypography>
              <TextField
                select
                value={filtroEstatus}
                onChange={(e) => setFiltroEstatus(e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="pendiente">Pendiente</MenuItem>
                <MenuItem value="pagada">Pagada</MenuItem>
                <MenuItem value="cancelada">Cancelada</MenuItem>
                <MenuItem value="anulada">Anulada</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MDTypography variant="caption" color="transparent" mb={0.5}>
                .
              </MDTypography>
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="gradient"
                  color={sidenavColor}
                  onClick={aplicarFiltros}
                  fullWidth
                  size="small"
                >
                  <Icon sx={{ mr: 0.5 }}>search</Icon>
                  Filtrar
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={limpiarFiltros}
                  size="small"
                  sx={{ minWidth: "auto", px: 1.5 }}
                >
                  <Icon>clear</Icon>
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      <Card>
        <MDBox p={{ xs: 2, sm: 3 }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDTypography variant="h6" fontWeight="bold">
              Lista de Ventas
            </MDTypography>
          </MDBox>
          {loading ? (
            <MDBox py={4} textAlign="center">
              <MDTypography variant="body2" color="text">
                Cargando ventas...
              </MDTypography>
            </MDBox>
          ) : (
            <DataTable
              table={ventas}
              isSorted={false}
              entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
              showTotalEntries={true}
              noEndBorder
            />
          )}
        </MDBox>
      </Card>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        <MenuItem onClick={verDetalle}>
          <Icon sx={{ mr: 1 }}>visibility</Icon>
          Ver Detalle
        </MenuItem>
        <MenuItem onClick={imprimirFactura}>
          <Icon sx={{ mr: 1 }}>print</Icon>
          Imprimir Factura
        </MenuItem>
        <MenuItem onClick={abrirModalAnular} sx={{ color: "error.main" }}>
          <Icon sx={{ mr: 1 }}>cancel</Icon>
          Anular Venta
        </MenuItem>
      </Menu>

      {/* Modal Detalle de Venta */}
      <Dialog
        open={modalDetalle}
        onClose={() => setModalDetalle(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon fontSize="large" color="info">
              receipt_long
            </Icon>
            <MDTypography variant="h5" fontWeight="bold">
              Detalle de Venta
            </MDTypography>
          </MDBox>
        </DialogTitle>

        <DialogContent>
          {detalleVenta && (
            <MDBox>
              {/* Información General */}
              <Card sx={{ mb: 2, p: 2, backgroundColor: "grey.50" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text">
                      Número de Factura
                    </MDTypography>
                    <MDTypography variant="h6" fontWeight="bold">
                      {detalleVenta.numero_factura || `VENTA-${detalleVenta.id}`}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text">
                      Fecha
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {new Date(detalleVenta.fecha_venta || detalleVenta.created_at).toLocaleString(
                        "es-ES"
                      )}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text">
                      Cliente
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium">
                      {detalleVenta.cliente
                        ? `${detalleVenta.cliente.nombre} ${detalleVenta.cliente.apellido || ""}`
                        : "Cliente General"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDTypography variant="caption" color="text">
                      Estatus
                    </MDTypography>
                    <MDBox>
                      <Chip
                        label={detalleVenta.estatus.toUpperCase()}
                        color={
                          detalleVenta.estatus === "pagada"
                            ? "success"
                            : detalleVenta.estatus === "pendiente"
                            ? "warning"
                            : "error"
                        }
                        size="small"
                      />
                    </MDBox>
                  </Grid>
                </Grid>
              </Card>

              {/* Productos */}
              <MDTypography variant="h6" fontWeight="bold" mb={1}>
                Productos
              </MDTypography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                  {detalleVenta.detalles?.map((detalle, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <MDTypography variant="caption" fontWeight="medium">
                          {detalle.producto?.nombre || "Producto"}
                        </MDTypography>
                      </TableCell>
                      <TableCell align="center">
                        <MDTypography variant="caption" color="text">
                          {detalle.cantidad} x {formatCurrency(parseFloat(detalle.precio_unitario))}
                        </MDTypography>
                      </TableCell>
                      <TableCell align="right">
                        <MDTypography variant="caption" fontWeight="bold">
                          {formatCurrency(
                            parseFloat(detalle.cantidad) * parseFloat(detalle.precio_unitario)
                          )}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />

              {/* Totales */}
              <MDBox>
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text">
                    Subtotal:
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {formatCurrency(parseFloat(detalleVenta.subtotal || 0))}
                  </MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="body2" color="text">
                    Impuesto:
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {formatCurrency(parseFloat(detalleVenta.impuesto || 0))}
                  </MDTypography>
                </MDBox>
                {detalleVenta.descuento > 0 && (
                  <MDBox display="flex" justifyContent="space-between" mb={1}>
                    <MDTypography variant="body2" color="text">
                      Descuento:
                    </MDTypography>
                    <MDTypography variant="body2" fontWeight="medium" color="error">
                      -{formatCurrency(parseFloat(detalleVenta.descuento))}
                    </MDTypography>
                  </MDBox>
                )}
                <Divider sx={{ my: 1 }} />
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  p={1.5}
                  sx={{ backgroundColor: "grey.100", borderRadius: 1 }}
                >
                  <MDTypography variant="h6" fontWeight="bold">
                    Total:
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="bold" color="success">
                    {formatCurrency(parseFloat(detalleVenta.total))}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="outlined" color="dark" onClick={() => setModalDetalle(false)}>
            <Icon sx={{ mr: 1 }}>close</Icon>
            Cerrar
          </MDButton>
          <MDButton variant="gradient" color={sidenavColor} onClick={imprimirFactura}>
            <Icon sx={{ mr: 1 }}>print</Icon>
            Imprimir
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Modal Anular Venta */}
      <Dialog
        open={modalAnular}
        onClose={() => setModalAnular(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: { xs: "90%", sm: 400 },
          },
        }}
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon fontSize="large" color="error">
              warning
            </Icon>
            <MDTypography variant="h5" fontWeight="medium">
              Anular Venta
            </MDTypography>
          </MDBox>
        </DialogTitle>

        <DialogContent>
          <MDTypography variant="body2" color="text" sx={{ mt: 1 }}>
            ¿Está seguro que desea anular la siguiente venta?
          </MDTypography>

          {ventaSeleccionada && (
            <Card
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "rgba(244, 67, 54, 0.1)",
                border: "1px solid rgba(244, 67, 54, 0.3)",
              }}
            >
              <MDTypography variant="h6" fontWeight="bold" color="dark">
                {ventaSeleccionada.numero_factura || `VENTA-${ventaSeleccionada.id}`}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Total: {formatCurrency(parseFloat(ventaSeleccionada.total))}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Cliente:{" "}
                {ventaSeleccionada.cliente
                  ? `${ventaSeleccionada.cliente.nombre} ${
                      ventaSeleccionada.cliente.apellido || ""
                    }`
                  : "Cliente General"}
              </MDTypography>
            </Card>
          )}

          <MDTypography variant="caption" color="error" sx={{ mt: 2, display: "block" }}>
            <strong>Advertencia:</strong> Esta acción no se puede deshacer. La venta será marcada
            como anulada.
          </MDTypography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="outlined" color="dark" onClick={() => setModalAnular(false)}>
            <Icon sx={{ mr: 1 }}>close</Icon>
            Cancelar
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={anularVenta}>
            <Icon sx={{ mr: 1 }}>cancel</Icon>
            Anular Venta
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
